import React, { useState } from 'react';
import { 
  CheckCircle, 
  ChevronRight, 
  CreditCard, 
  FileText, 
  Users, 
  Calendar, 
  DollarSign, 
  Clock, 
  Package, 
  Star 
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { useSalesContext } from '../../contexts/SalesContext';
import { formatCurrency } from '../../utils/calculations';
import FinancialSimulator from '../../components/sales/FinancialSimulator';

const SalesFlowPage = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customerData, setCustomerData] = useState({});
  const [paymentData, setPaymentData] = useState({});
  
  const { campaigns } = useSalesContext();
  
  const steps = [
    { id: 1, name: 'Produto', icon: Package },
    { id: 2, name: 'Cliente', icon: Users },
    { id: 3, name: 'Pagamento', icon: DollarSign },
    { id: 4, name: 'Contrato', icon: FileText },
    { id: 5, name: 'Assinatura', icon: CheckCircle },
    { id: 6, name: 'Conclusão', icon: Star }
  ];
  
  const products = [
    { id: 1, name: 'Torre Sul - 1004', type: 'Fração', weekNumber: 14, status: 'available', price: 65000 },
    { id: 2, name: 'Torre Sul - 1004', type: 'Fração', weekNumber: 15, status: 'available', price: 68000 },
    { id: 3, name: 'Torre Sul - 1004', type: 'Apartamento Completo', weekNumber: null, status: 'available', price: 780000 },
    { id: 4, name: 'Torre Norte - 505', type: 'Fração', weekNumber: 22, status: 'available', price: 55000 }
  ];
  
  const handleNextStep = () => {
    if (activeStep < steps.length) {
      setActiveStep(activeStep + 1);
    }
  };
  
  const handlePreviousStep = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };
  
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
  };
  
  const handleCustomerDataChange = (e) => {
    setCustomerData({
      ...customerData,
      [e.target.name]: e.target.value
    });
  };
  
  const handlePaymentSimulationComplete = (data) => {
    setPaymentData(data);
    handleNextStep();
  };
  
  return (
    <MainLayout>
      <div className="bg-gray-100 rounded-lg overflow-hidden shadow-lg">
        {/* Header */}
        <div className="bg-white p-4 border-b">
          <h2 className="text-xl font-bold text-blue-900">Processo de Venda</h2>
        </div>
        
        {/* Progress Bar */}
        <div className="bg-white p-4 border-b">
          <div className="flex items-center">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div 
                  className={`flex flex-col items-center ${step.id !== steps.length ? 'flex-1' : ''}`}
                  onClick={() => step.id <= activeStep && setActiveStep(step.id)}
                >
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center 
                    ${step.id < activeStep ? 'bg-green-600 text-white' : 
                      step.id === activeStep ? 'bg-blue-900 text-white' : 
                      'bg-gray-200 text-gray-500'}
                    ${step.id <= activeStep ? 'cursor-pointer' : ''}
                  `}>
                    {step.id < activeStep ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-xs mt-1">{step.name}</span>
                </div>
                
                {index < steps.length - 1 && (
                  <div className="flex-1 h-1 bg-gray-200">
                    <div 
                      className="h-full bg-green-600"
                      style={{ width: step.id < activeStep ? '100%' : '0%' }}
                    ></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          {/* Step 1: Product Selection */}
          {activeStep === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                <h3 className="font-bold text-blue-900 mb-2">Selecione o Produto</h3>
                
                <div className="grid grid-cols-1 gap-3 mb-3">
                  {products.map(product => (
                    <div 
                      key={product.id}
                      className={`p-3 rounded-md border cursor-pointer transition-colors ${
                        selectedProduct?.id === product.id 
                          ? 'bg-blue-100 border-blue-500' 
                          : 'bg-white border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => handleSelectProduct(product)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-gray-800">{product.name}</h4>
                          <p className="text-sm text-gray-500">
                            {product.type === 'Fração' 
                              ? `${product.type} - Semana ${product.weekNumber}` 
                              : product.type}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-blue-900">{formatCurrency(product.price)}</span>
                          <p className="text-xs text-green-600">Disponível</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-blue-200 pt-3">
                  <h4 className="font-medium text-blue-900 mb-2">Campanhas Disponíveis</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {campaigns.map(campaign => (
                      <div 
                        key={campaign.id}
                        className="flex items-center p-2 bg-white rounded border border-gray-200"
                      >
                        <input 
                          type="radio" 
                          id={`campaign-${campaign.id}`} 
                          name="campaign"
                          className="mr-2"
                        />
                        <label htmlFor={`campaign-${campaign.id}`} className="flex-1 cursor-pointer">
                          <div className="flex justify-between">
                            <span className="font-medium text-sm">{campaign.name}</span>
                            <span className="text-sm text-red-600 font-bold">-{campaign.discountPercentage}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-500">
                              {campaign.bonus && `+ ${campaign.bonus}`}
                            </span>
                            <span className="text-xs text-gray-500">
                              <Clock className="inline w-3 h-3 mr-1" />
                              Válido até {new Date(campaign.validUntil).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 2: Customer Data */}
          {activeStep === 2 && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold text-blue-900">Dados do Cliente</h3>
                  <button className="text-sm text-blue-700 flex items-center">
                    Buscar Cliente Existente <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Nome Completo</label>
                    <input 
                      type="text" 
                      name="fullName"
                      value={customerData.fullName || ''}
                      onChange={handleCustomerDataChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">CPF</label>
                    <input 
                      type="text" 
                      name="cpf"
                      value={customerData.cpf || ''}
                      onChange={handleCustomerDataChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">E-mail</label>
                    <input 
                      type="email" 
                      name="email"
                      value={customerData.email || ''}
                      onChange={handleCustomerDataChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Telefone</label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={customerData.phone || ''}
                      onChange={handleCustomerDataChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 3: Payment */}
          {activeStep === 3 && (
            <div className="space-y-4">
              {selectedProduct && (
                <FinancialSimulator 
                  productValue={selectedProduct.price}
                  onSimulationComplete={handlePaymentSimulationComplete}
                />
              )}
            </div>
          )}
          
          {/* Step 4: Contract */}
          {activeStep === 4 && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold text-blue-900">Contrato</h3>
                  <div className="flex space-x-2">
                    <button className="text-sm text-blue-700 flex items-center">
                      <FileText className="w-4 h-4 mr-1" /> Visualizar
                    </button>
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border border-gray-200 mb-4">
                  <h4 className="font-medium text-blue-900 mb-2">Resumo da Venda</h4>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <span className="text-xs text-gray-500 block">Cliente</span>
                      <span className="text-sm font-medium">{customerData.fullName || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">CPF</span>
                      <span className="text-sm font-medium">{customerData.cpf || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Produto</span>
                      <span className="text-sm font-medium">
                        {selectedProduct 
                          ? `${selectedProduct.name} (${selectedProduct.type === 'Fração' ? 'Semana ' + selectedProduct.weekNumber : selectedProduct.type})`
                          : 'N/A'
                        }
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Valor Total</span>
                      <span className="text-sm font-medium text-blue-900">
                        {selectedProduct 
                          ? formatCurrency(paymentData?.simulation?.totalValue || selectedProduct.price)
                          : 'N/A'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 5: Signature */}
          {activeStep === 5 && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                <h3 className="font-bold text-blue-900 mb-3">Assinatura Digital</h3>
                
                <div className="bg-white p-4 rounded border border-gray-200 mb-4 text-center">
                  <FileText className="w-16 h-16 mx-auto text-blue-900 mb-3" />
                  <h4 className="font-medium text-gray-800 mb-1">Contrato pronto para assinatura</h4>
                  <p className="text-sm text-gray-600 mb-4">O contrato já foi gerado e está pronto para assinatura digital.</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 6: Conclusion */}
          {activeStep === 6 && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-5 rounded-md border border-blue-200 text-center">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10" />
                </div>
                
                <h3 className="font-bold text-blue-900 text-xl mb-2">Venda Concluída com Sucesso!</h3>
                <p className="text-gray-600 mb-6">A venda foi realizada e registrada no sistema.</p>
                
                <div className="bg-white p-4 rounded border border-gray-200 mb-6">
                  <h4 className="font-medium text-gray-800 mb-3">Resumo da Transação</h4>
                  <div className="grid grid-cols-2 gap-y-2 text-left mb-3">
                    <div className="text-sm text-gray-500">Cliente:</div>
                    <div className="text-sm font-medium">{customerData.fullName || 'N/A'}</div>
                    
                    <div className="text-sm text-gray-500">Produto:</div>
                    <div className="text-sm font-medium">
                      {selectedProduct 
                        ? `${selectedProduct.name} (${selectedProduct.type === 'Fração' ? 'Semana ' + selectedProduct.weekNumber : selectedProduct.type})`
                        : 'N/A'
                      }
                    </div>
                    
                    <div className="text-sm text-gray-500">Valor:</div>
                    <div className="text-sm font-medium text-blue-900">
                      {selectedProduct 
                        ? formatCurrency(paymentData?.simulation?.totalValue || selectedProduct.price)
                        : 'N/A'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Navigation */}
          <div className="flex justify-between mt-6">
            <button
              onClick={handlePreviousStep}
              className={`px-4 py-2 border border-blue-900 text-blue-900 rounded-md ${activeStep === 1 ? 'invisible' : ''}`}
            >
              Anterior
            </button>
            
            <button
              onClick={handleNextStep}
              disabled={activeStep === 1 && !selectedProduct}
              className={`px-4 py-2 bg-blue-900 text-white rounded-md ${
                activeStep === 1 && !selectedProduct ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {activeStep === steps.length ? 'Finalizar' : 'Próximo'}
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SalesFlowPage;